import {zx} from 'shared/type'
import {z} from 'zod'
import {zRoles} from './roleTypes'
import {P, match} from 'ts-pattern'
import {zodResolver} from '@mantine/form'
import {zLicense, zUsage} from './license'
import {pick} from 'remeda'

export namespace WorkspaceTypes {
  export const GLOBAL_ID = 'global'

  export const zDomainCfgNew = z.object({
    domain: z.string().default(''),
    roles: z.array(zRoles).default([])
  })

  export const zDomainCfg = z.object({
    domain: z.preprocess(
      v =>
        match(v)
          .with(P.nullish, () => undefined)
          .with(P.string, s => s.toLowerCase())
          .otherwise(v => String(v).toLowerCase()),
      z.string().regex(/^[a-z0-9-]+(\.[a-z0-9-]+)*$/i, 'invalid domain')
    ),
    roles: z.array(zRoles).default([])
  })

  const zInviteLinkShape = {
    name: z.string().default(''),
    roles: z.array(zRoles).default([]),
    anon: z.boolean().optional(),
    join: z.boolean().optional(),
    noPrompt: z.boolean().optional(),
    /** The path for shared URLs, e.g. "/questions" or "/question/123" */
    path: zx.nullish.string,
    /** @deprecated Use path instead. Kept for backward compatibility */
    question_id: zx.nullish.string,
    created: z.string(),
    disabled: zx.nullish.bool,
    expires: z.string().optional()
  }

  export const zInviteLinkCfgRaw = z.object(zInviteLinkShape)
  export const zInviteLinkCfg = z.object(zInviteLinkShape).transform(i => inviteLinkCfgTransform(i))

  type InviteLinkCfgRaw = z.infer<typeof zInviteLinkCfgRaw>
  export const inviteLinkCfgTransform = <T extends Pick<InviteLinkCfgRaw, 'question_id' | 'path'>>({
    question_id,
    ...rest
  }: T) => (question_id && !rest.path ? {...rest, path: `/question/${question_id}`} : rest)

  export type InviteLinkCfg = Omit<InviteLinkCfgRaw, 'question_id'>

  export const zInviteLinkNew = z.object({
    ...pick(zInviteLinkShape, ['name', 'anon', 'noPrompt', 'expires', 'path']),
    roles: z.array(zRoles).default(['forecaster'])
  })
  export type InviteLinkNew = z.infer<typeof zInviteLinkNew>

  export const zRequest = z.object({
    email: z.string(),
    status: z.enum(['requested', 'add', 'ignore']).default('requested'),
    updatedAt: z.string().optional(),
    updatedBy: z.string().optional(),
    requestAt: z.string(),
    msg: z.string().default('')
  })

  export type Request = z.infer<typeof zRequest>

  export const defaultFeatures = {
    'comparison-search': true
  }

  const zFeatures = z
    .object({
      'comparison-search': z.boolean().default(true)
    })
    .default(defaultFeatures)

  export const limitMd = {
    'no-limit': 'No limit',
    plan: 'Limit to plan',
    custom: 'Custom limit'
  }
  const zLimitType = zx.enumFromObjKeys(limitMd).default('no-limit')
  export const zLimit = z.object({
    type: zLimitType,
    value: z.number().optional()
  })

  export type Limit = z.infer<typeof zLimit>

  export const zQuestionOrder = z.object({
    active: z.array(z.string()).default([]),
    inactive: z.array(z.string()).default([])
  })
  export type QuestionOrder = z.infer<typeof zQuestionOrder>

  /** schema when initially creating a new workspace*/
  export const zCfg = z.object({
    name: z.string().default(''),
    description: z.string().default(''),
    hideIdentities: z.boolean().default(false),
    domains: z.record(zDomainCfg).default({}),
    inviteLinks: z.record(zInviteLinkCfg).default({}),
    requests: z.record(zRequest).default({}),
    owner: z.string().optional(),
    enableFeedback: z.boolean().default(false), // false for existing data, but can be overridden hen making new workspaces in the ui
    archived: z.boolean().default(false),
    advancedUI: z.boolean().default(false), // extra features we add for ourselves but generally don't show for others
    features: zFeatures,
    logActivity: z.boolean().default(true),
    createdByTest: z.boolean().optional(),
    limitUsers: zLimit.optional(),
    limitParticipants: zLimit.optional(),
    questionOrder: zQuestionOrder.default({active: [], inactive: []})
  })
  export type Cfg = z.infer<typeof zCfg>
  export const cfgValidator = zodResolver(zCfg.extend({name: z.string().min(3, 'Name must be at least 3 characters')}))

  export const getZRow = <T extends Cfg>(schema: z.ZodType<T, any, any>) =>
    z.object({
      id: z.string(),
      cfg: schema,
      created_by: z.string(),
      created_at: z.string(),
      updated_at: z.string(),
      deleted: zx.nullish.bool
    })

  export const zRow = getZRow(zCfg)

  export type Row<T extends Cfg = Cfg> = z.infer<ReturnType<typeof getZRow<T>>>

  export const zGlobalCfg = z.object({
    ...zCfg.shape,
    upgrades: z
      .record(
        z.object({
          updated: z.string(),
          status: z.enum(['error', 'complete', 'pending', 'ready']),
          error: z.string().optional()
        })
      )
      .default({})
  })
  export type GlobalCfg = z.infer<typeof zGlobalCfg>

  export type UpgradeCfg = GlobalCfg['upgrades'][string]

  /** The additional admin info to do with managing a workspace. Global admin to write, admin to read */
  export const zGlobalAdmin = z.object({
    license: zLicense,
    stripe: z
      .object({
        customerId: z.string(),
        subscriptionId: z.string().optional(),
        current_period_start: zx.nullish.num,
        current_period_end: zx.nullish.num,
        status: zx.nullish.string,
        cancel_at: zx.nullish.num
      })
      .nullish(),
    usage: zUsage.optional()
  })
  export type GlobalAdmin = z.infer<typeof zGlobalAdmin>
  export const zGlobalAdminRow = z.object({
    id: z.string(),
    cfg: zGlobalAdmin,
    created_by: z.string(),
    created_at: z.string(),
    updated_at: z.string(),
    updated_by: z.string()
  })
  export type GlobalAdminRow = z.infer<typeof zGlobalAdminRow>
}
